import React, { useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { IsolatedTextarea } from "@/components/common/IsolatedTextarea";
// 15/07/2026 — Classi shadcn Textarea replicate per IsolatedTextarea
// (che è un `<textarea>` plain). Mantiene identico l'aspetto visivo.
const SHADCN_TEXTAREA_CN = "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
import { confirmDirtyClose } from "@/lib/dialogConfirm";
import { Search, MessageSquare, Edit2, Plus, X, Filter, Printer, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/api";
import { cn } from "@/lib/utils";
import { usePagination } from "@/lib/usePagination";

// Stati pratica considerati "chiusi": le note di queste pratiche vengono
// spostate nella sezione collassabile in fondo, allineato al pattern di
// Diario Giornaliero / Lavoro Quotidiano / Scadenze. Coerente con la
// costante `CLOSED_STATUSES` di `RubricaClientiTab`.
const CLOSED_STATUSES = new Set(["Chiusa", "Conclusa"]);

/**
 * Note Pratica Tab - Operational consultation register
 * 
 * Features:
 * - Compact table-like/Excel layout
 * - Editable notes
 * - Quick filters: Oggi, Ieri, Ultimi 7 giorni
 * - Search by: text, client, operator, practice number
 * - Supports: phone calls, WhatsApp, emails, in-person meetings
 */
function NoteInterneTab({ 
  cases = [], 
  setCases,
  clients = [], 
  operators = [],
  getOperatorName,
  currentOperator,
  operatorRole = "amministratore"
}) {
  // Determine default management filter based on role
  const defaultMgmt = operatorRole === "operatore_studio" ? "studio" 
    : operatorRole === "operatore_agenzia" ? "agenzia" 
    : "all";

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [managementFilter, setManagementFilter] = useState(defaultMgmt);
  
  // Edit dialog state
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState("");
  const [addingNoteForCase, setAddingNoteForCase] = useState(null);
  const [newNoteText, setNewNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // New Note dialog state (create note from this page)
  const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [createNoteText, setCreateNoteText] = useState("");
  // Use simple select instead of Popover/Command to avoid DOM errors
  const [caseSearchTerm, setCaseSearchTerm] = useState("");

  // Get today's date boundaries
  const getDateBoundaries = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return { today, yesterday, sevenDaysAgo, tomorrow: new Date(today.getTime() + 86400000) };
  };

  // Collect all notes from all practices and flatten into a single list
  // 16/07/2026 — Fase 2.2 audit: pre-calcolo `Map<clientId, client>`
  // per portare la lookup da O(N × M) a O(N + M). Con 500 pratiche +
  // 300 clienti passiamo da ~150.000 iterazioni a ~800.
  const clientsById = useMemo(() => {
    const m = new Map();
    for (const cl of clients) {
      if (cl && cl.id) m.set(cl.id, cl);
    }
    return m;
  }, [clients]);

  const allNotes = useMemo(() => {
    const notes = [];

    cases.forEach(caseItem => {
      const client = clientsById.get(caseItem.clientId);
      const clientName = client?.fullName || "Cliente sconosciuto";
      
      // Get structured notes
      if (caseItem.structuredNotes && Array.isArray(caseItem.structuredNotes)) {
        caseItem.structuredNotes.forEach((note, noteIndex) => {
          notes.push({
            id: `${caseItem.id}-${noteIndex}`,
            caseId: caseItem.id,
            caseNumber: caseItem.caseNumber,
            practiceType: caseItem.practiceType,
            caseStatus: caseItem.status,
            clientName,
            clientId: caseItem.clientId,
            operatorName: note.operatorName || "Sconosciuto",
            operatorId: note.operatorId,
            text: note.text,
            timestamp: note.timestamp,
            noteIndex
          });
        });
      }
      
      // Also include legacy internalNotes if no structured notes
      if ((!caseItem.structuredNotes || caseItem.structuredNotes.length === 0) && caseItem.internalNotes) {
        notes.push({
          id: `${caseItem.id}-legacy`,
          caseId: caseItem.id,
          caseNumber: caseItem.caseNumber,
          practiceType: caseItem.practiceType,
          caseStatus: caseItem.status,
          clientName,
          clientId: caseItem.clientId,
          operatorName: "Sistema",
          operatorId: null,
          text: caseItem.internalNotes,
          timestamp: caseItem.updatedAt || caseItem.createdAt || new Date().toISOString(),
          noteIndex: -1,
          isLegacy: true
        });
      }
    });
    
    return notes;
  }, [cases, clientsById]);

  // Get all operators for filter (use passed operators prop, not just from notes)
  const operatorOptions = useMemo(() => {
    // Use all operators from the operators prop
    if (operators && operators.length > 0) {
      return operators.map(op => ({
        id: op.id,
        name: op.fullName || op.name || "Operatore"
      })).sort((a, b) => a.name.localeCompare(b.name));
    }
    // Fallback: extract from notes if operators prop not available
    const ops = new Map();
    allNotes.forEach(n => {
      if (n.operatorId && !ops.has(n.operatorId)) {
        ops.set(n.operatorId, n.operatorName);
      } else if (!n.operatorId && n.operatorName && !ops.has(n.operatorName)) {
        ops.set(n.operatorName, n.operatorName);
      }
    });
    return Array.from(ops.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [operators, allNotes]);

  // Get unique clients from notes
  const uniqueClients = useMemo(() => {
    const cls = new Map();
    allNotes.forEach(n => {
      if (!cls.has(n.clientId)) {
        cls.set(n.clientId, n.clientName);
      }
    });
    return Array.from(cls.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allNotes]);

  // Filter notes based on all criteria
  const filteredNotes = useMemo(() => {
    const { today, yesterday, sevenDaysAgo, tomorrow } = getDateBoundaries();
    
    return allNotes.filter(note => {
      // Management filter
      if (managementFilter !== "all") {
        const targetType = managementFilter === "agenzia" ? "Gestionale Agenzia" : "Gestionale Studio";
        if (note.practiceType !== targetType) return false;
      }
      
      // Date filter
      if (dateFilter !== "all") {
        const noteDate = new Date(note.timestamp);
        if (dateFilter === "oggi" && (noteDate < today || noteDate >= tomorrow)) return false;
        if (dateFilter === "ieri" && (noteDate < yesterday || noteDate >= today)) return false;
        if (dateFilter === "7giorni" && noteDate < sevenDaysAgo) return false;
      }
      
      // Client filter
      if (clientFilter !== "all" && note.clientId !== clientFilter) return false;
      
      // Operator filter
      if (operatorFilter !== "all" && note.operatorName !== operatorFilter) return false;
      
      // Text search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesText = note.text.toLowerCase().includes(query);
        const matchesClient = note.clientName.toLowerCase().includes(query);
        const matchesOperator = note.operatorName.toLowerCase().includes(query);
        const matchesCaseNumber = note.caseNumber.toLowerCase().includes(query);
        if (!matchesText && !matchesClient && !matchesOperator && !matchesCaseNumber) return false;
      }
      
      return true;
    });
  }, [allNotes, dateFilter, clientFilter, operatorFilter, managementFilter, searchQuery]);

  // Sort notes by timestamp descending (newest first) - this is the primary and only sort
  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      // Sort by timestamp only - newest first
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  }, [filteredNotes]);

  // Split: note di pratiche attive vs note di pratiche chiuse.
  // Allineato al pattern di Diario / Lavoro Quotidiano / Scadenze:
  // quando la pratica passa in "Chiusa/Completata/Archiviata" le sue
  // note vengono spostate nella sezione collassabile in fondo, così
  // la lista operativa principale resta pulita.
  const activeNotes = useMemo(
    () => sortedNotes.filter((n) => !CLOSED_STATUSES.has(n.caseStatus)),
    [sortedNotes]
  );
  const closedNotes = useMemo(
    () => sortedNotes.filter((n) => CLOSED_STATUSES.has(n.caseStatus)),
    [sortedNotes]
  );

  // Sezione "Note di pratiche chiuse": collassata di default.
  const [showClosedNotes, setShowClosedNotes] = useState(false);

  // Paginazione globale (50 default + jump + selettore 10/30/50/100).
  // Due istanze indipendenti — preferenza per-operatore persistita
  // tramite `usePagination` (localStorage).
  const {
    paged: pagedActiveNotes,
    paginationBar: activeNotesPaginationBar,
  } = usePagination(activeNotes, {
    storageKey: "note-pratica-attive",
    itemLabelSingular: "nota",
    itemLabelPlural: "note",
  });
  const {
    paged: pagedClosedNotes,
    paginationBar: closedNotesPaginationBar,
  } = usePagination(closedNotes, {
    storageKey: "note-pratica-chiuse",
    itemLabelSingular: "nota",
    itemLabelPlural: "note",
  });

  // Handle editing a note
  const handleEditNote = useCallback((note) => {
    setEditingNote(note);
    setEditText(note.text);
  }, []);

  // Save edited note
  const handleSaveEdit = useCallback(async () => {
    if (!editingNote || !editText.trim()) return;
    
    setIsSaving(true);
    
    try {
      const caseItem = cases.find(c => c.id === editingNote.caseId);
      if (!caseItem) throw new Error("Case not found");
      
      const updatedNotes = [...(caseItem.structuredNotes || [])];
      if (editingNote.noteIndex >= 0 && updatedNotes[editingNote.noteIndex]) {
        updatedNotes[editingNote.noteIndex] = {
          ...updatedNotes[editingNote.noteIndex],
          text: editText.trim()
        };
      }
      
      // Update legacy format
      const legacyFormat = updatedNotes.map(n => 
        `[${new Date(n.timestamp).toLocaleString('it-IT')} - ${n.operatorName}] ${n.text}`
      ).join('\n');
      
      const updatedCase = {
        ...caseItem,
        structuredNotes: updatedNotes,
        internalNotes: legacyFormat
      };
      delete updatedCase.id;
      delete updatedCase.createdAt;
      delete updatedCase.updatedAt;
      
      await api.updateCase(editingNote.caseId, updatedCase);
      
      setCases(prev => prev.map(c => 
        c.id === editingNote.caseId 
          ? { ...c, structuredNotes: updatedNotes, internalNotes: legacyFormat }
          : c
      ));
      
      setEditingNote(null);
      setEditText("");
    } catch (error) {
      console.error("Failed to save note:", error);
      alert("Errore nel salvare la nota");
    } finally {
      setIsSaving(false);
    }
  }, [editingNote, editText, cases, setCases]);

  // Handle adding a new note to a practice
  const handleAddNote = useCallback(async () => {
    if (!addingNoteForCase || !newNoteText.trim()) return;
    
    setIsSaving(true);
    
    const operatorName = currentOperator?.fullName || currentOperator?.name || "Utente";
    const newNote = {
      text: newNoteText.trim(),
      operatorId: currentOperator?.id || null,
      operatorName: operatorName,
      timestamp: new Date().toISOString()
    };
    
    try {
      const caseItem = cases.find(c => c.id === addingNoteForCase);
      if (!caseItem) throw new Error("Case not found");
      
      const updatedNotes = [...(caseItem.structuredNotes || []), newNote];
      
      const legacyFormat = updatedNotes.map(n => 
        `[${new Date(n.timestamp).toLocaleString('it-IT')} - ${n.operatorName}] ${n.text}`
      ).join('\n');
      
      const updatedCase = {
        ...caseItem,
        structuredNotes: updatedNotes,
        internalNotes: legacyFormat
      };
      delete updatedCase.id;
      delete updatedCase.createdAt;
      delete updatedCase.updatedAt;
      
      await api.updateCase(addingNoteForCase, updatedCase);
      
      setCases(prev => prev.map(c => 
        c.id === addingNoteForCase 
          ? { ...c, structuredNotes: updatedNotes, internalNotes: legacyFormat }
          : c
      ));
      
      setAddingNoteForCase(null);
      setNewNoteText("");
    } catch (error) {
      console.error("Failed to add note:", error);
      alert("Errore nell'aggiungere la nota");
    } finally {
      setIsSaving(false);
    }
  }, [addingNoteForCase, newNoteText, currentOperator, cases, setCases]);

  // Handle creating a new note from this page (with practice selection)
  const handleCreateNewNote = useCallback(async () => {
    if (!selectedCaseId || !createNoteText.trim()) return;
    
    setIsSaving(true);
    
    const operatorName = currentOperator?.fullName || currentOperator?.name || "Utente";
    const newNote = {
      text: createNoteText.trim(),
      operatorId: currentOperator?.id || null,
      operatorName: operatorName,
      timestamp: new Date().toISOString()
    };
    
    try {
      const caseItem = cases.find(c => c.id === selectedCaseId);
      if (!caseItem) throw new Error("Pratica non trovata");
      
      const updatedNotes = [...(caseItem.structuredNotes || []), newNote];
      
      const legacyFormat = updatedNotes.map(n => 
        `[${new Date(n.timestamp).toLocaleString('it-IT')} - ${n.operatorName}] ${n.text}`
      ).join('\n');
      
      const updatedCase = {
        ...caseItem,
        structuredNotes: updatedNotes,
        internalNotes: legacyFormat
      };
      delete updatedCase.id;
      delete updatedCase.createdAt;
      delete updatedCase.updatedAt;
      
      await api.updateCase(selectedCaseId, updatedCase);
      
      // Update local state - note will appear in both practice and this list
      setCases(prev => prev.map(c => 
        c.id === selectedCaseId 
          ? { ...c, structuredNotes: updatedNotes, internalNotes: legacyFormat }
          : c
      ));
      
      // Reset dialog
      setShowNewNoteDialog(false);
      setSelectedCaseId("");
      setCreateNoteText("");
      setCaseSearchTerm("");
    } catch (error) {
      console.error("Failed to create note:", error);
      alert("Errore nella creazione della nota");
    } finally {
      setIsSaving(false);
    }
  }, [selectedCaseId, createNoteText, currentOperator, cases, setCases]);

  // Get case info for display
  const getSelectedCaseInfo = () => {
    if (!selectedCaseId) return null;
    const caseItem = cases.find(c => c.id === selectedCaseId);
    if (!caseItem) return null;
    const client = clients.find(c => c.id === caseItem.clientId);
    return {
      caseNumber: caseItem.caseNumber,
      clientName: client?.fullName || "Cliente",
      subject: caseItem.subject
    };
  };

  // Filter cases for search
  const filteredCasesForSearch = useMemo(() => {
    if (!caseSearchTerm) return cases;
    const term = caseSearchTerm.toLowerCase();
    return cases.filter(c => {
      const client = clients.find(cl => cl.id === c.clientId);
      return c.caseNumber.toLowerCase().includes(term) ||
             c.subject?.toLowerCase().includes(term) ||
             client?.fullName?.toLowerCase().includes(term);
    });
  }, [cases, clients, caseSearchTerm]);

  // Stats
  const totalNotes = filteredNotes.length;
  const totalAllNotes = allNotes.length;

  // Print filtered notes
  const handlePrintNotes = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const filterInfo = [];
    if (dateFilter !== "all") {
      const labels = { oggi: "Oggi", ieri: "Ieri", "7giorni": "Ultimi 7 giorni" };
      filterInfo.push(`Periodo: ${labels[dateFilter]}`);
    }
    if (clientFilter !== "all") {
      const client = uniqueClients.find(([id]) => id === clientFilter);
      filterInfo.push(`Cliente: ${client ? client[1] : clientFilter}`);
    }
    if (operatorFilter !== "all") {
      filterInfo.push(`Operatore: ${operatorFilter}`);
    }
    if (searchQuery) {
      filterInfo.push(`Ricerca: "${searchQuery}"`);
    }

    const notesHtml = sortedNotes.map(note => {
      const noteDate = new Date(note.timestamp);
      return `
        <tr>
          <td style="padding: 6px; border: 1px solid #ddd;">${noteDate.toLocaleDateString('it-IT')}</td>
          <td style="padding: 6px; border: 1px solid #ddd;">${noteDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</td>
          <td style="padding: 6px; border: 1px solid #ddd; font-family: monospace;">${note.caseNumber}</td>
          <td style="padding: 6px; border: 1px solid #ddd;">${note.clientName}</td>
          <td style="padding: 6px; border: 1px solid #ddd;">${note.text}</td>
          <td style="padding: 6px; border: 1px solid #ddd;">${note.operatorName}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Note Pratica - Stampa</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .filter-info { font-size: 12px; color: #666; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #f5f5f5; padding: 8px; border: 1px solid #ddd; text-align: left; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Note Pratica</h1>
        <p class="filter-info">
          Stampato il ${new Date().toLocaleString('it-IT')} - ${sortedNotes.length} note
          ${filterInfo.length > 0 ? '<br/>Filtri: ' + filterInfo.join(' | ') : ''}
        </p>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Ora</th>
              <th>Nr Pratica</th>
              <th>Cliente</th>
              <th>Contenuto Nota</th>
              <th>Operatore</th>
            </tr>
          </thead>
          <tbody>
            ${notesHtml}
          </tbody>
        </table>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [sortedNotes, dateFilter, clientFilter, operatorFilter, searchQuery, uniqueClients]);

  return (
    <>
      {/* Header */}
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Note Pratica
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {totalNotes === totalAllNotes 
                ? `${totalNotes} note totali`
                : `${totalNotes} di ${totalAllNotes} note`
              }
              {closedNotes.length > 0 && (
                <span className="text-xs text-slate-400 ml-2">
                  · {activeNotes.length} attive · {closedNotes.length} di pratiche chiuse
                </span>
              )}
            </p>
          </div>
          
          {/* Search bar */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per testo, cliente, operatore, pratica..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9"
                data-testid="notes-search-input"
              />
            </div>
            
            {/* Nuova Nota button */}
            <Button
              onClick={() => setShowNewNoteDialog(true)}
              className="h-9"
              data-testid="new-note-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nuova Nota
            </Button>
            
            {/* Print filtered notes button */}
            <Button
              variant="outline"
              onClick={handlePrintNotes}
              className="h-9"
              data-testid="print-notes-button"
              disabled={sortedNotes.length === 0}
              title={`Stampa ${sortedNotes.length} righe filtrate`}
            >
              <Printer className="h-4 w-4 mr-1" />
              Stampa
              <span className="ml-1 text-xs opacity-70">({sortedNotes.length})</span>
            </Button>
          </div>
        </div>
        
        {/* Quick filters and dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Quick date filters */}
          <div className="flex items-center gap-1 mr-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-500 mr-1">Periodo:</span>
            <Button
              size="sm"
              variant={dateFilter === "oggi" ? "default" : "outline"}
              className="h-7 text-xs px-2"
              onClick={() => setDateFilter(dateFilter === "oggi" ? "all" : "oggi")}
            >
              Oggi
            </Button>
            <Button
              size="sm"
              variant={dateFilter === "ieri" ? "default" : "outline"}
              className="h-7 text-xs px-2"
              onClick={() => setDateFilter(dateFilter === "ieri" ? "all" : "ieri")}
            >
              Ieri
            </Button>
            <Button
              size="sm"
              variant={dateFilter === "7giorni" ? "default" : "outline"}
              className="h-7 text-xs px-2"
              onClick={() => setDateFilter(dateFilter === "7giorni" ? "all" : "7giorni")}
            >
              Ultimi 7 giorni
            </Button>
            {dateFilter !== "all" && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs px-1"
                onClick={() => setDateFilter("all")}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          {/* Management filter */}
          <Select value={managementFilter} onValueChange={setManagementFilter}>
            <SelectTrigger className={cn(
              "h-7 w-[130px] text-xs font-medium",
              managementFilter === "studio" && "border-blue-400 bg-blue-50 text-blue-700",
              managementFilter === "agenzia" && "border-orange-400 bg-orange-50 text-orange-700"
            )}>
              <SelectValue placeholder="Gestione" />
            </SelectTrigger>
            <SelectContent>
              {(operatorRole === "amministratore" || operatorRole === "responsabile") && <SelectItem value="all" className="text-xs">Tutte</SelectItem>}
              <SelectItem value="agenzia" className="text-xs">Agenzia</SelectItem>
              <SelectItem value="studio" className="text-xs">Studio</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Client filter */}
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-7 w-[150px] text-xs">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Tutti i clienti</SelectItem>
              {uniqueClients
                .filter(([id]) => id && id !== "")
                .map(([id, name]) => (
                  <SelectItem key={id} value={id} className="text-xs">{name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          
          {/* Operator filter - uses ALL operators, not just from notes */}
          <Select value={operatorFilter} onValueChange={setOperatorFilter}>
            <SelectTrigger className="h-7 w-[150px] text-xs">
              <SelectValue placeholder="Operatore" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Tutti gli operatori</SelectItem>
              {operatorOptions
                .filter(op => op.name && op.name !== "")
                .map(op => (
                  <SelectItem key={op.id || op.name} value={op.name} className="text-xs">{op.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          
          {/* Clear all filters */}
          {(dateFilter !== "all" || clientFilter !== "all" || operatorFilter !== "all" || searchQuery) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-red-600"
              onClick={() => {
                setDateFilter("all");
                setClientFilter("all");
                setOperatorFilter("all");
                setSearchQuery("");
              }}
            >
              Rimuovi filtri
            </Button>
          )}
        </div>
      </div>

      {/* Helper render: una singola tabella di note (riusata per attive
          e chiuse). Restituisce JSX inline; il flag `closedView` rende
          le righe più tenui visivamente. */}
      {(() => {
        const renderRow = (note, closedView = false) => {
          const noteDate = new Date(note.timestamp);
          const dateStr = noteDate.toLocaleDateString('it-IT');
          const timeStr = noteDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
          return (
            <TableRow key={note.id} className={closedView ? "hover:bg-emerald-50/40 text-slate-600" : "hover:bg-slate-50"}>
              <TableCell className="py-1.5 text-xs text-slate-600">{dateStr}</TableCell>
              <TableCell className="py-1.5 text-xs text-slate-600">{timeStr}</TableCell>
              <TableCell className="py-1.5">
                <Badge variant="outline" className="font-mono text-[10px]">
                  {note.caseNumber}
                </Badge>
              </TableCell>
              <TableCell className="py-1.5 text-xs font-medium text-slate-700 truncate max-w-[150px]">
                {note.clientName}
              </TableCell>
              <TableCell className="py-1.5 text-xs text-slate-700">
                <span className="line-clamp-3 whitespace-pre-line">{note.text}</span>
              </TableCell>
              <TableCell className="py-1.5 text-xs font-medium text-blue-600">
                {note.operatorName}
              </TableCell>
              <TableCell className="py-1.5 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => handleEditNote(note)}
                    disabled={note.isLegacy}
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                  {!closedView && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        setAddingNoteForCase(note.caseId);
                        setNewNoteText("");
                      }}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        };
        const tableHeader = (
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[85px] text-xs font-semibold">Data</TableHead>
              <TableHead className="w-[50px] text-xs font-semibold">Ora</TableHead>
              <TableHead className="w-[80px] text-xs font-semibold">Nr Pratica</TableHead>
              <TableHead className="w-[150px] text-xs font-semibold">Cliente</TableHead>
              <TableHead className="text-xs font-semibold">Contenuto Nota</TableHead>
              <TableHead className="w-[100px] text-xs font-semibold">Operatore</TableHead>
              <TableHead className="w-[70px] text-xs font-semibold text-center">Azioni</TableHead>
            </TableRow>
          </TableHeader>
        );
        return (
          <>
            {/* ─── Note di pratiche ATTIVE ─── */}
            <Card className="overflow-hidden" data-testid="notes-active-section">
              <Table>
                {tableHeader}
                <TableBody>
                  {activeNotes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center">
                        <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">
                          {searchQuery || dateFilter !== "all" || clientFilter !== "all" || operatorFilter !== "all"
                            ? "Nessuna nota trovata con i filtri selezionati"
                            : "Nessuna nota di pratica attiva"
                          }
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedActiveNotes.map((n) => renderRow(n, false))
                  )}
                </TableBody>
              </Table>
              <div className="px-3">{activeNotesPaginationBar}</div>
            </Card>

            {/* ─── Note di pratiche CHIUSE — collassate di default ─── */}
            {closedNotes.length > 0 && (
              <Card className="overflow-hidden border-slate-200 mt-4" data-testid="notes-closed-section">
                <button
                  type="button"
                  onClick={() => setShowClosedNotes((s) => !s)}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                  data-testid="notes-closed-toggle"
                >
                  <div className="flex items-center gap-2">
                    {showClosedNotes
                      ? <ChevronDown className="h-4 w-4 text-slate-500" />
                      : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    <span className="text-sm font-medium text-slate-700">
                      Note di pratiche chiuse
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                      {closedNotes.length}
                    </Badge>
                  </div>
                  <span className="text-xs text-slate-400">
                    {showClosedNotes ? "Nascondi" : "Mostra"}
                  </span>
                </button>
                {showClosedNotes && (
                  <>
                    <Table>
                      {tableHeader}
                      <TableBody>
                        {pagedClosedNotes.map((n) => renderRow(n, true))}
                      </TableBody>
                    </Table>
                    <div className="px-3">{closedNotesPaginationBar}</div>
                  </>
                )}
              </Card>
            )}
          </>
        );
      })()}

      {/* Edit Note Dialog */}
      <Dialog open={!!editingNote} onOpenChange={(open) => !open && setEditingNote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-500">
              Pratica: <Badge variant="outline" className="font-mono">{editingNote?.caseNumber}</Badge>
              <span className="ml-2">{editingNote?.clientName}</span>
            </div>
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              placeholder="Contenuto della nota..."
              asChild
            >
              <IsolatedTextarea
                value={editText}
                onCommit={setEditText}
                rows={4}
                placeholder="Contenuto della nota..."
                className={SHADCN_TEXTAREA_CN}
                data-testid="note-edit-textarea"
              />
            </Textarea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNote(null)}>
              Annulla
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving || !editText.trim()}>
              {isSaving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={!!addingNoteForCase} onOpenChange={(open) => !open && setAddingNoteForCase(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {(() => {
              const caseItem = cases.find(c => c.id === addingNoteForCase);
              const client = clients.find(c => c.id === caseItem?.clientId);
              return (
                <div className="text-sm text-slate-500">
                  Pratica: <Badge variant="outline" className="font-mono">{caseItem?.caseNumber}</Badge>
                  <span className="ml-2">{client?.fullName || "Cliente"}</span>
                </div>
              );
            })()}
            <IsolatedTextarea
              value={newNoteText}
              onCommit={setNewNoteText}
              rows={4}
              placeholder="Inserisci nota (telefonata, WhatsApp, email, incontro)..."
              className={SHADCN_TEXTAREA_CN}
              data-testid="note-add-textarea"
            />
            {currentOperator && (
              <p className="text-xs text-slate-400">
                Firmata da: {currentOperator.fullName || currentOperator.name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingNoteForCase(null)}>
              Annulla
            </Button>
            <Button onClick={handleAddNote} disabled={isSaving || !newNoteText.trim()}>
              {isSaving ? "Salvataggio..." : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Note Dialog (create from this page with practice selection) */}
      <Dialog
        open={showNewNoteDialog}
        onOpenChange={(o) => {
          if (!o) {
            const dirty = Boolean(selectedCaseId || (createNoteText && createNoteText.trim()));
            if (dirty && !confirmDirtyClose(true)) return;
          }
          setShowNewNoteDialog(o);
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Nuova Nota
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Practice selection - using native Select to avoid DOM errors */}
            <div className="space-y-2">
              <Label>Seleziona Pratica *</Label>
              {/* Search input */}
              <Input
                placeholder="Filtra pratiche per numero, cliente, oggetto..."
                value={caseSearchTerm}
                onChange={(e) => setCaseSearchTerm(e.target.value)}
                className="mb-2"
              />
              {/* Practice select */}
              <Select value={selectedCaseId} onValueChange={setSelectedCaseId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleziona una pratica..." />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {filteredCasesForSearch
                    .filter((caseItem) => caseItem.id && caseItem.id !== "")
                    .slice(0, 30)
                    .map((caseItem) => {
                      const client = clients.find(c => c.id === caseItem.clientId);
                      return (
                        <SelectItem key={caseItem.id} value={caseItem.id}>
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-xs text-blue-600">{caseItem.caseNumber}</span>
                            <span className="truncate">{client?.fullName || "Cliente"}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              {selectedCaseId && getSelectedCaseInfo() && (
                <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                  <span className="font-medium">{getSelectedCaseInfo()?.caseNumber}</span>
                  {" - "}
                  {getSelectedCaseInfo()?.clientName}
                  {getSelectedCaseInfo()?.subject && (
                    <span className="block text-slate-400 truncate">{getSelectedCaseInfo()?.subject}</span>
                  )}
                </div>
              )}
            </div>
            
            {/* Note text */}
            <div className="space-y-2">
              <Label>Contenuto Nota *</Label>
              <IsolatedTextarea
                value={createNoteText}
                onCommit={setCreateNoteText}
                rows={4}
                placeholder="Inserisci nota (telefonata, WhatsApp, email, incontro)..."
                className={SHADCN_TEXTAREA_CN}
                data-testid="note-create-textarea"
              />
            </div>
            
            {currentOperator && (
              <p className="text-xs text-slate-400">
                Firmata da: {currentOperator.fullName || currentOperator.name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowNewNoteDialog(false);
                setSelectedCaseId("");
                setCreateNoteText("");
              }}
            >
              Annulla
            </Button>
            <Button 
              onClick={handleCreateNewNote} 
              disabled={isSaving || !selectedCaseId || !createNoteText.trim()}
            >
              {isSaving ? "Salvataggio..." : "Salva Nota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default React.memo(NoteInterneTab);
